import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { deleteObject, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "@/firebase";

interface SampleMaterial {
  id: string;
  sampleName: string;
  storagePath: string;
  isCommon: boolean;
  isActive: boolean;
}

const emptyForm = {
  sampleName: "",
  storagePath: "",
  isCommon: false,
  isActive: true,
};

export default function SampleMaterialsPage() {
  const [materials, setMaterials] = useState<SampleMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [form, setForm] = useState(emptyForm);

  const sortedMaterials = useMemo(
    () =>
      [...materials].sort((a, b) => a.sampleName.localeCompare(b.sampleName, "ja")),
    [materials]
  );

  const fetchMaterials = async () => {
    setLoading(true);
    setError(null);
    try {
      const snapshot = await getDocs(collection(db, "sampleMaterials"));
      const data = snapshot.docs.map((docSnap) => {
        const docData = docSnap.data() as Omit<SampleMaterial, "id">;
        return {
          id: docSnap.id,
          sampleName: docData.sampleName || "",
          storagePath: docData.storagePath || "",
          isCommon: Boolean(docData.isCommon),
          isActive: Boolean(docData.isActive),
        };
      });
      setMaterials(data);
    } catch (err) {
      console.error("Failed to load sample materials", err);
      setError("サンプル資料の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setSelectedFile(null);
    setEditingId(null);
  };

  const openModal = (material?: SampleMaterial) => {
    if (material) {
      setEditingId(material.id);
      setForm({
        sampleName: material.sampleName,
        storagePath: material.storagePath,
        isCommon: material.isCommon,
        isActive: material.isActive,
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (file.type !== "application/pdf") {
      alert("PDFファイルのみアップロードできます。");
      event.target.value = "";
      return;
    }
    setSelectedFile(file);
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.sampleName.trim()) {
      alert("サンプル資料名を入力してください。");
      return;
    }
    if (!selectedFile && !form.storagePath) {
      alert("PDFファイルをアップロードしてください。");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        let nextStoragePath = form.storagePath;
        if (selectedFile) {
          nextStoragePath = `admin/sample/${editingId}/${selectedFile.name}`;
          await uploadBytes(ref(storage, nextStoragePath), selectedFile);
          if (form.storagePath && form.storagePath !== nextStoragePath) {
            await deleteObject(ref(storage, form.storagePath));
          }
        }
        await updateDoc(doc(db, "sampleMaterials", editingId), {
          sampleName: form.sampleName.trim(),
          storagePath: nextStoragePath,
          isCommon: form.isCommon,
          isActive: form.isActive,
        });
      } else {
        if (!selectedFile) {
          throw new Error("PDF file missing");
        }
        const docRef = await addDoc(collection(db, "sampleMaterials"), {
          sampleName: form.sampleName.trim(),
          storagePath: "",
          isCommon: form.isCommon,
          isActive: form.isActive,
        });
        const storagePath = `admin/sample/${docRef.id}/${selectedFile.name}`;
        await uploadBytes(ref(storage, storagePath), selectedFile);
        await updateDoc(docRef, { storagePath });
      }
      closeModal();
      await fetchMaterials();
    } catch (err) {
      console.error("Failed to save sample material", err);
      alert("サンプル資料の保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (material: SampleMaterial) => {
    if (!confirm("本当に削除しますか?")) return;
    try {
      await deleteDoc(doc(db, "sampleMaterials", material.id));
      if (material.storagePath) {
        await deleteObject(ref(storage, material.storagePath));
      }
      await fetchMaterials();
    } catch (err) {
      console.error("Failed to delete sample material", err);
      alert("サンプル資料の削除に失敗しました。");
    }
  };

  const handleCopy = async (storagePath: string) => {
    try {
      await navigator.clipboard.writeText(storagePath);
      alert("storagePathをコピーしました。");
    } catch (err) {
      console.error("Failed to copy storage path", err);
      alert("コピーに失敗しました。");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">サンプル資料管理</h2>
          <p className="text-sm text-gray-500">メール添付用のサンプル資料(PDF)を管理します。</p>
        </div>
        <button
          type="button"
          onClick={() => openModal()}
          className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
        >
          新規作成
        </button>
      </div>

      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">サンプル資料一覧</h3>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-gray-500">読み込み中...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-500">{error}</div>
        ) : sortedMaterials.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">登録済みのサンプル資料はありません。</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">サンプル資料名</th>
                  <th className="px-4 py-3 text-left font-semibold">storagePath</th>
                  <th className="px-4 py-3 text-left font-semibold">共通資料</th>
                  <th className="px-4 py-3 text-left font-semibold">利用状況</th>
                  <th className="px-4 py-3 text-left font-semibold">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedMaterials.map((material) => (
                  <tr key={material.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900 font-medium">{material.sampleName}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-2">
                        <span className="text-xs text-gray-500 break-all">{material.storagePath}</span>
                        <button
                          type="button"
                          onClick={() => handleCopy(material.storagePath)}
                          className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold"
                        >
                          コピー
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                          material.isCommon
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {material.isCommon ? "共通" : "個別"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                          material.isActive
                            ? "bg-blue-50 text-blue-600"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {material.isActive ? "利用中" : "停止"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openModal(material)}
                          className="px-3 py-1 rounded-md text-xs font-semibold text-emerald-600 bg-emerald-50 hover:bg-emerald-100"
                        >
                          編集
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(material)}
                          className="px-3 py-1 rounded-md text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100"
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-lg">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingId ? "サンプル資料を編集" : "サンプル資料を作成"}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700">サンプル資料名</label>
                <input
                  type="text"
                  value={form.sampleName}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, sampleName: event.target.value }))
                  }
                  className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  placeholder="例: 介護福祉士 入門ガイド"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700">PDFファイル</label>
                <div className="mt-2 space-y-2">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-600 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-emerald-50 file:text-emerald-600 hover:file:bg-emerald-100"
                  />
                  <p className="text-xs text-gray-500">
                    {selectedFile
                      ? `選択済み: ${selectedFile.name}`
                      : "PDF未選択の場合は既存のstoragePathが利用されます。"}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700">storagePath</label>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    value={form.storagePath || "(未設定)"}
                    readOnly
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500"
                  />
                  <button
                    type="button"
                    onClick={() => form.storagePath && handleCopy(form.storagePath)}
                    className="px-3 py-2 rounded-lg text-xs font-semibold text-emerald-600 bg-emerald-50 hover:bg-emerald-100"
                    disabled={!form.storagePath}
                  >
                    コピー
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.isCommon}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, isCommon: event.target.checked }))
                    }
                    className="h-4 w-4 text-emerald-600 border-gray-300 rounded"
                  />
                  共通資料として扱う
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, isActive: event.target.checked }))
                    }
                    className="h-4 w-4 text-emerald-600 border-gray-300 rounded"
                  />
                  利用中
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
                >
                  {saving ? "保存中..." : "保存"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
